import { api } from '@http/constants';
import fetchWrapper from '@/http/fetchWrapper';
import { useQuery } from '@tanstack/react-query';
import {
  governanceIssuesFilterParams,
  type GovernanceIssuesFilters,
} from '@http/queries/useFetchGovernanceIssues';

export type GovernanceIssuesGroupBy =
  | 'policy'
  | 'team'
  | 'page'
  | 'domain'
  | 'priority';

/**
 * One bucket of the filtered issue set. `key` is the stable identity to match
 * issues against (policy id, url, domain, or priority). `count` covers the
 * whole filtered set, not just the pages loaded so far.
 */
export interface GovernanceIssueGroupResponse {
  key: string;
  label: string | null;
  color?: string | null;
  count: number;
}

/**
 * All groups of the current user's filtered governance issues with their total
 * counts, independent of the paginated issue stream.
 */
export function useFetchGovernanceIssueGroups(
  groupBy: GovernanceIssuesGroupBy | null,
  filters: GovernanceIssuesFilters = {},
) {
  return useQuery({
    queryKey: [api.governanceIssues, 'groups', groupBy, filters],
    queryFn: () => {
      const params = governanceIssuesFilterParams(filters);
      params.set('groupBy', String(groupBy));

      return fetchWrapper.get<GovernanceIssueGroupResponse[]>(
        `${api.governanceIssues}/groups`,
        params,
      );
    },
    enabled: groupBy !== null,
    // Match the issues query so both stay consistent on arrival.
    refetchOnMount: 'always',
  });
}
